import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";
import Select from "react-select";
import config from "../../../config"

const AgeScrollDropdown = ({ value, onChange, options, placeholder }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const selected = options.find((opt) => String(opt.value) === String(value));
  const displayLabel = selected ? selected.label : placeholder;

  return (
    <div className="relative w-full sm:w-[120px] md:w-[140px] lg:w-[140px]" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="w-full h-9 px-2 border border-gray-300 rounded-xl text-sm md:text-base lg:text-lg bg-white text-left flex items-center justify-between focus:ring-2 focus:ring-indigo-500"
      >
        <span className={selected ? "text-blue-950" : "text-gray-500"}>
          {displayLabel}
        </span>
        <span className="text-gray-500 text-xs">&#9662;</span>
      </button>

      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-[216px] overflow-y-auto">
          {options.map((opt) => {
            const isActive = String(opt.value) === String(value);
            return (
              <button
                key={String(opt.value)}
                type="button"
                onClick={() => {
                  onChange(String(opt.value));
                  setOpen(false);
                }}
                className={`w-full text-left px-3 py-2 text-sm md:text-base lg:text-lg transition ${
                  isActive
                    ? "bg-orange-50 text-orange-600 font-medium"
                    : "text-blue-950 hover:bg-gray-50"
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

  const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    borderRadius: '0.7rem',
    borderColor: state.isFocused ? '#f97316' : '#e5e7eb',
    boxShadow: state.isFocused ? '0 0 0 2px #fed7aa' : 'none',
    minHeight: '46px',
    '&:hover': { borderColor: '#f97316' }
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    borderRadius: '0.75rem',
    overflow: 'hidden'
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? '#f97316'
      : state.isFocused
      ? '#fff7ed'
      : 'white',
    color: state.isSelected ? 'white' : '#374151'
  })
};

const BasicinfopetForm = ({ petToEdit, onSave }) => {
  const token = localStorage.getItem("token");
  const decoded = token ? jwtDecode(token) : null;
  const client_id = decoded?.data?.customer_data?.id || "";
  const [petTypes, setPetTypes] = useState([]);
  const [petBreeds, setPetBreeds] = useState([]);
  const [petSizes, setPetSizes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [initialPetData, setInitialPetData] = useState(null);
  const [years, setYears] = useState("0");
  const [months, setMonths] = useState("0");
  const [days, setDays] = useState("0");
  const [ageMode, setAgeMode] = useState("dob");
  const [manualAgeEdited, setManualAgeEdited] = useState(false);
  const [manualAge, setManualAge] = useState({
    years: "",
    months: "",
    days: "",
  });
  const [showColorInput, setShowColorInput] = useState(false);
  const [customColor, setCustomColor] = useState("");

  const [colors, setColors] = useState([
    "black",
    "white",
    "brown",
    "yellow",
    "orange",
    "blue",
  ]);

  const [petData, setPetData] = useState({
    name: "",
    pet_type: "",
    breed: "",
    gender: "",
    size: "",
    color: "",
    birth_date: "",
    age: "",
    weight_kg: "",
    neutered_spayed: "",
    vaccination_status: "",
    hair_type: "",
    hair_length: "",
    meal_preferences: [],
    meals_per_day: "",
    walks_per_day: "",
    behaviour: "",
  });

  /* ---------------- FETCH MASTER DATA ---------------- */
  useEffect(() => {
    fetchPetTypes();
    fetchBreeds();
    fetchSizes();
  }, []);
  const mealOptions = [
    { label: "Vegetarian", value: "Vegetarian", color: "green" },
    { label: "Non-Vegetarian", value: "Non-Vegetarian", color: "red" },
    { label: "Home-Cooked Diet", value: "Home-Cooked Diet", color: "green" },
    { label: "Mixed Diet", value: "Mixed Diet", color: "red" },
    { label: "Prescription Diet", value: "Prescription Diet", color: "green" },
    { label: "Raw Diet", value: "Raw Diet", color: "red" },
  ];

  // Helper to extract size value - API returns size as ID, need to lookup from petSizes
  const extractSizeValue = (pet, sizesArray) => {
    if (!pet) return "";

    // First check for size_label field
    if (pet.size_label) return pet.size_label;

    // Then check if size is an object with size_label
    if (typeof pet.size === "object" && pet.size?.size_label)
      return pet.size.size_label;

    // If size is a string, check if it's an ID and lookup from sizesArray
    if (typeof pet.size === "string" && sizesArray && sizesArray.length > 0) {
      // Try to find size by ID
      const foundSize = sizesArray.find((s) => s._id === pet.size);
      if (foundSize?.size_label) {
        return foundSize.size_label;
      }

      // If not found by ID, maybe it's already a label
      const labelMatch = ["small", "medium", "large"];
      if (labelMatch.includes(pet.size.toLowerCase())) {
        return pet.size;
      }
    }

    return pet.size || "";
  };

  // Helper to extract hair_length value
  const extractHairLength = (pet) => {
    if (!pet) return "";

    if (typeof pet.hair_length === "object" && pet.hair_length?.label)
      return pet.hair_length.label;
    if (typeof pet.hair_length === "string") return pet.hair_length;
    return "";
  };


  useEffect(() => {
  if (!petToEdit) return;

  const ageStr = petToEdit.age || "";
  const birthday = petToEdit.birthday;

  // Change "0" to ""
  const y = ageStr.match(/(\d+)y/)?.[1] || ""; 
  const m = ageStr.match(/(\d+)m/)?.[1] || "";
  const d = ageStr.match(/(\d+)d/)?.[1] || "";

  setYears(y);
  setMonths(m);
  setDays(d);

  setManualAge({
    years: y,
    months: m,
    days: d,
  });

  setAgeMode(birthday ? "dob" : "manual");
}, [petToEdit]);

  useEffect(() => {
    if (!petToEdit) {
      setInitialPetData(petData); // ADD mode
      return;
    }
    

    const hairLengthValue = extractHairLength(petToEdit);

    const updatedData = {
      name: petToEdit.name || "",
      pet_type: petToEdit.pet_type?._id || petToEdit.pet_type || "",
      breed: petToEdit.breed?._id || petToEdit.breed || "",
      gender: normalizeFieldValue("gender", petToEdit.gender),
      size: petToEdit.size?._id || petToEdit.size || "",
      color: normalizeFieldValue("color", petToEdit.color),
      birth_date:
        petToEdit.birthday?.slice(0, 10) ||
        petToEdit.birth_date?.slice(0, 10) ||
        "",
      age: petToEdit.age || "",
      weight_kg: petToEdit.weight_kg ?? "",
      neutered_spayed: normalizeFieldValue(
        "neutered_spayed",
        petToEdit.neutered_spayed
      ),
      vaccination_status: normalizeFieldValue(
        "vaccination_status",
        petToEdit.vaccination_status
      ),
      hair_type: normalizeFieldValue("hair_type", petToEdit.hair_type),
      hair_length: normalizeFieldValue("hair_length", hairLengthValue),
      meal_preferences: petToEdit.meal_preferences || [],
      meals_per_day:
        petToEdit.meals_per_day !== undefined
          ? String(petToEdit.meals_per_day)
          : "",
      walks_per_day:
        petToEdit.walks_per_day !== undefined
          ? String(petToEdit.walks_per_day)
          : "",
      behaviour: petToEdit.behavior_notes || petToEdit.behaviour || "",
    };

    setPetData(updatedData);
    setInitialPetData(updatedData);
    setIsDirty(false);
  }, [petToEdit, petSizes]);

  useEffect(() => {
    fetchPetTypes();
    fetchBreeds();
  }, []);

  useEffect(() => {
    if (petData.pet_type) {
      fetchSizes(petData.pet_type);
    } else {
      setPetSizes([]);
    }
  }, [petData.pet_type]);

  const fetchPetTypes = async () => {
    try {
      const res = await axios.get(
        `${config.API_BASE_URL}/customer/customerpettype`
      );
      setPetTypes(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch {
      setPetTypes([]);
    }
  };

  const fetchBreeds = async () => {
    try {
      const res = await axios.get(
        `${config.API_BASE_URL}/customer/customerbreed`
      );
      if (res.data?.status) setPetBreeds(res.data.data);
    } catch {
      toast.error("Failed to fetch breeds");
    }
  };

  const fetchSizes = async (petTypeId) => {
    if (!petTypeId) {
      setPetSizes([]);
      return;
    }

    try {
      const res = await axios.get(
        `${config.API_BASE_URL}/customer/customerpetsize`,
        {
          params: { pet_type: petTypeId },
        }
      );

      if (res.data?.status) {
        setPetSizes(res.data.data || []);
      }
    } catch {
      toast.error("Failed to fetch pet sizes");
      setPetSizes([]);
    }
  };

  const handleDob = (e) => {
  const date = e.target.value;
  handleChange("birth_date", date);

  if (!date) return;

  // Parse date manually to avoid timezone issue
  const [year, month, day] = date.split("-").map(Number);
  const birth = new Date(year, month - 1, day);
  const today = new Date();

  if (birth > today) {
    toast.error("Date of birth cannot be in the future");
    return;
  }

  let years = today.getFullYear() - birth.getFullYear();
  let months = today.getMonth() - birth.getMonth();
  let days = today.getDate() - birth.getDate();

  if (days < 0) {
    months--;
    const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
    days += prevMonth.getDate();
  }

  if (months < 0) {
    years--;
    months += 12;
  }

  setYears(String(years));
  setMonths(String(months));
  setDays(String(days));

  setManualAge({
    years: String(years),
    months: String(months),
    days: String(days),
  });

  handleChange("age", `${years}y ${months}m ${days}d`);
  console.log("Calculated:", years, months, days);
};

  const handleManualAge = (field, value) => {
    let num = Math.max(0, Number(value) || 0);
    if (field === "months") {
  num = Math.min(11, num);
} else if (field === "days") {
  num = Math.min(30, num);     // ← add this
}
  setManualAgeEdited(true);
    setManualAge((prev) => ({ ...prev, [field]: num }));
     
    const y = field === "years" ? num : manualAge.years || 0;
    const m = field === "months" ? num : manualAge.months || 0;
    const d = field === "days" ? num : manualAge.days || 0;

    setYears(String(y));
    setMonths(String(m));
    setDays(String(d));

    handleChange("age", `${y}y ${m}m ${d}d`);

    if (manualAgeEdited || true) {   // true is just to make sure it clears after first edit
    handleChange("birth_date", "");
  }
  };

  const normalizeCase = (value) => {
    if (!value || typeof value !== "string") return "";
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  };

  const normalizeTitleCase = (value) => {
    if (!value || typeof value !== "string") return "";
    return value
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const normalizeFieldValue = (field, value) => {
    if (!value || typeof value !== "string") return "";

    const valueLower = value.toLowerCase().trim();

    switch (field) {
      case "gender":
        if (valueLower === "male") return "Male";
        if (valueLower === "female") return "Female";
        return normalizeCase(value);

      case "size":
        if (valueLower === "small") return "Small";
        if (valueLower === "medium") return "Medium";
        if (valueLower === "large") return "Large";
        return normalizeCase(value);

      case "hair_length":
        if (valueLower === "short" || valueLower === "short hair")
          return "Short";
        if (valueLower === "medium" || valueLower === "medium hair")
          return "Medium";
        if (valueLower === "long" || valueLower === "long hair") return "Long";
        return normalizeCase(value);

      case "hair_type":
        const hairTypes = {
          smooth: "Smooth",
          wire: "Wire",
          double: "Double",
          hairless: "Hairless",
          curly: "Curly",
          wavy: "Wavy",
          "drop coat": "Drop Coat",
          dropcoat: "Drop Coat",
        };
        return hairTypes[valueLower] || normalizeTitleCase(value);

      case "neutered_spayed":
        const neuteredMap = {
          "not neutered": "Not Neutered",
          notneutered: "Not Neutered",
          neutered: "Neutered",
          unknown: "Unknown",
        };
        return neuteredMap[valueLower] || normalizeTitleCase(value);

      case "vaccination_status":
        const vaccineMap = {
          vaccinated: "Vaccinated",
          "not vaccinated": "Not Vaccinated",
          notvaccinated: "Not Vaccinated",
          unknown: "Unknown",
        };
        return vaccineMap[valueLower] || normalizeTitleCase(value);

      case "color":
        return valueLower;

      default:
        return value;
    }
  };

  useEffect(() => {
  if (ageMode === "dob") {
    // When switching back to DOB mode → reset the "edited" flag
    // so next time user goes to manual without changing anything → DOB is kept
    setManualAgeEdited(false);
  }
}, [ageMode]);


  const filteredBreeds = petBreeds.filter(
    (b) => b.pet_type_id === petData.pet_type
  );

  const handleChange = (field, value) => {
    setPetData((prev) => {
      const updated = { ...prev, [field]: value };
      if (initialPetData) {
        const hasChanged =
          JSON.stringify(updated) !== JSON.stringify(initialPetData);
        setIsDirty(hasChanged);
      }
      return updated;
    });
  };

  const handleSave = async () => {

    if (petData.weight_kg && Number(petData.weight_kg) <= 0) {
      toast.error("Weight must be greater than 0");
      setLoading(false);
      return;
    }
  
    try {
      setLoading(true);

      // Build clean payload for backend
      const payload = {
        client_id,
        name: petData.name?.trim(),
        pet_type: petData.pet_type,
        breed: petData.breed,
        gender: petData.gender?.toLowerCase(), // backend likely expects "male"/"female"
        size: petData.size,
        color: petData.color,
        birthday: petData.birth_date || undefined, // send only if exists
        age: petData.age || undefined, // ← CRITICAL: Keep age!
        weight_kg: petData.weight_kg ? Number(petData.weight_kg) : undefined,
        neutered_spayed: petData.neutered_spayed,
        vaccination_status: petData.vaccination_status,
        hair_type: petData.hair_type,
        hair_length: petData.hair_length,
        meal_preferences: petData.meal_preferences, // array of strings
        meals_per_day: petData.meals_per_day
          ? Number(petData.meals_per_day)
          : undefined,
        walks_per_day: petData.walks_per_day
          ? Number(petData.walks_per_day)
          : undefined,
        behavior_notes: petData.behaviour?.trim() || undefined,
      };

      // Remove undefined fields to avoid sending them
      Object.keys(payload).forEach((key) => {
        if (payload[key] === undefined || payload[key] === "") {
          delete payload[key];
        }
      });

      console.log("Sending payload:", payload); // ← Remove in production

      let response;
      if (petToEdit) {
        response = await axios.put(
          `${config.API_BASE_URL}/customer/updatepet/${petToEdit._id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        response = await axios.post(
          `${config.API_BASE_URL}/customer/customeraddpet`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      // Better success detection
      if (
        response.data &&
        (response.data.success === true ||
          response.data.status === true ||
          response.status === 200 ||
          response.status === 201)
      ) {
        toast.success(`Pet ${petToEdit ? "updated" : "added"} successfully!`);
        onSave?.();
        return;
      }

      // If we reach here, it's likely an error
      const errorMsg =
        response.data?.message || response.data?.error || "Unknown error";
      toast.error(
        typeof errorMsg === "object" ? JSON.stringify(errorMsg) : errorMsg
      );
    } catch (error) {
      console.error("Save error:", error);
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Network error. Please try again.";
      toast.error(Array.isArray(msg) ? msg.join(", ") : msg);
    } finally {
      setLoading(false);
    }
  };

  const formatAge = (y, m, d) =>
  "Age: " +
  (
    [
      +y > 0 && `${+y} ${+y === 1 ? "year" : "years"}`,
      +m > 0 && `${+m} ${+m === 1 ? "month" : "months"}`,
      +d > 0 && `${+d} ${+d === 1 ? "day" : "days"}`
    ].filter(Boolean).join(" ") || "0 days"
  );

  return (
    <div className="bg-gray-50/50">
      <div className="space-y-2">
        {/* Card 1: Basic Information */}
        <div className="premium-card bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-gray-100 to-gray-100 px-3 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-white/20 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-blue-600">Basic Information</h2>
            </div>
          </div>
        </div>
        
        <div className="p-3">
          {/* 2-Column Layout for better space utilization */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 lg:gap-6">
            {/* Pet Name - Left column */}
            <div>
              <label className="text-sm md:text-base lg:text-lg font-semibold text-gray-700 block mb-2 flex items-center gap-2">
                <span className="w-0.5 h-4 bg-orange-500 rounded-full"></span>
                Pet Name
              </label>
              <input
                className="w-full lg:flex rounded-xl border border-gray-300 px-4 py-3 text-gray-700 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all premium-input shadow-sm"
                placeholder="Enter pet name"
                value={petData.name}
                onChange={(e) => handleChange("name", e.target.value)}
              />
            </div>

            {/* Pet Type - Right column */}
            <div>
              <label className="text-sm md:text-base lg:text-lg font-semibold text-gray-700 block mb-2 flex items-center gap-2 capitalize">
                <span className="w-0.5 h-4 bg-orange-500 rounded-full"></span>
                {petData.name ? (
                  <span>
                    <span className="text-orange-600 font-bold">{petData.name}</span>
                    <span className="text-gray-600">'s Type</span>
                  </span>
                ) : (
                  "Pet Type"
                )}
              </label>
              <div className="w-full rounded-xl border border-gray-300 px-4 py-3 text-gray-700 bg-gradient-to-r from-gray-50 to-white shadow-sm">
                {petTypes.find(p => p._id === petData.pet_type)?.name || (
                  <span className="text-gray-400">Select type</span>
                )}
              </div>
            </div>

            {/* Breed - Now also half width, not full width */}
            <div>
              <label className="text-sm md:text-base lg:text-lg font-semibold text-gray-700 block mb-2 flex items-center gap-2 capitalize">
                <span className="w-0.5 h-4 bg-orange-500 rounded-full"></span>
                {petData.name ? (
                  <span>
                    <span className="text-orange-600 font-bold">{petData.name}</span>
                    <span className="text-gray-600">'s Breed</span>
                  </span>
                ) : (
                  "Breed"
                )}
              </label>
              <Select
                options={filteredBreeds}
                value={filteredBreeds.find(b => b._id === petData.breed) || null}
                onChange={(option) => handleChange("breed", option?._id || "")}
                getOptionLabel={(option) => option.breed_name}
                getOptionValue={(option) => option._id}
                placeholder={filteredBreeds.length === 0 ? "No breeds available" : "Search breeds..."}
                isClearable
                isSearchable
                isDisabled={filteredBreeds.length === 0}
                noOptionsMessage={() => "No breeds found"}
                className="react-select-container"
                classNamePrefix="react-select"
                menuPortalTarget={document.body}
                menuPosition="fixed"

                styles={{
                  control: (base, state) => ({
                    ...base,
                    borderRadius: '0.75rem',
                    padding: '0px',
                    borderColor: state.isFocused ? '#f97316' : '#e5e7eb',
                    boxShadow: state.isFocused ? '0 0 0 2px #fed7aa' : 'none',
                    '&:hover': {
                      borderColor: '#f97316'
                    },
                    minHeight: '46px'
                  }),
                  placeholder: (base) => ({
                    ...base,
                    color: '#9ca3af'
                  }),
                  menu: (base) => ({
                    ...base,
                    borderRadius: '0.75rem',
                    overflow: 'hidden',
                    boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)',
                    zIndex: 30
                  }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isSelected ? '#f97316' : state.isFocused ? '#fff7ed' : 'white',
                    color: state.isSelected ? 'white' : '#374151',
                    padding: '10px 12px',
                    '&:active': {
                      backgroundColor: '#fed7aa'
                    }
                  })
                }}
              />
            </div>

            {/* Gender - Right column, aligning with Breed */}
            <div>
              <label className="text-sm md:text-base lg:text-lg font-semibold text-gray-700 block mb-3 flex items-center gap-2 capitalize">
                <span className="w-0.5 h-4 bg-orange-500 rounded-full"></span>
                {petData.name ? (
                  <span>
                    <span className="text-orange-600 font-bold">{petData.name}</span>
                    <span className="text-gray-600">'s Gender</span>
                  </span>
                ) : (
                  "Gender"
                )}
              </label>
              <div className="flex gap-3">
                {["Male", "Female"].map((gender) => {
                  const active = petData.gender === gender;
                  return (
                    <button
                      key={gender}
                      type="button"
                      onClick={() => handleChange("gender", gender)}
                      className={`flex-1 px-3 py-2 rounded-xl text-sm md:text-base lg:text-lg font-medium transition-all duration-200 ${
                        active
                          ? "bg-gradient-to-r from-orange-400 to-orange-500 text-white shadow-md shadow-orange-200"
                          : "bg-gray-50 text-gray-600 border border-gray-200 hover:bg-orange-50 hover:border-orange-300"
                      }`}
                    >
                      <div className="flex items-center justify-center gap-2">
                        {gender === "Male" ? "♂" : "♀"}
                        <span>{gender}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

        {/* Card 2: Physical Profile */}
        <div className="premium-card bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mt-2">
            {/* Header */}
            <div className="bg-gradient-to-r from-gray-100 to-gray-100 px-3 py-3 sm:px-3 py-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <h2 className="text-xl  font-bold text-blue-600">Physical Profile</h2>
              </div>
            </div>
            
            <div className="p-3 space-y-2">
              {/* Size & Weight in one row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-12 md:gap-8">
                {/* Size */}
                <div>
                  <label className="text-sm md:text-base lg:text-lg font-semibold text-gray-700 block mb-3 flex items-center gap-2 capitalize">
                    <span className="w-0.5 h-4 bg-orange-500 rounded-full"></span>
                    {petData.name ? (
                      <span>
                        <span className="text-orange-600 font-bold">{petData.name}</span>
                        <span className="text-gray-600">'s Size</span>
                      </span>
                    ) : (
                      "Size"
                    )}
                  </label>
                  <div className="hidden lg:grid grid-cols-4 gap-2">
                    {petSizes.map((s) => {
                      const active = petData.size === s._id;
                      return (
                        <button
                          key={s._id}
                          type="button"
                          onClick={() => handleChange("size", s._id)}
                          className={` px-2 py-1.5 rounded-lg text-sm md:text-sm lg:text-base font-medium transition-all duration-200 ${
                            active
                              ? "bg-gradient-to-r from-orange-400 to-orange-500 text-white shadow-md"
                              : "bg-gray-50 text-gray-600 border border-gray-200 hover:bg-orange-50"
                          }`}
                        >
                          {s.size_label}
                        </button>
                      );
                    })}
                  </div>
                  <div className="lg:hidden">
                    <Select
                      options={petSizes.map((s) => ({
                        value: s._id,
                        label: s.size_label
                      }))}
                      value={
                        petData.size
                          ? petSizes
                              .map((s) => ({ value: s._id, label: s.size_label }))
                              .find((opt) => opt.value === petData.size)
                        : null
                    }
                    onChange={(option) =>
                      handleChange("size", option?.value || "")
                    }
                    placeholder="Select Size"
                    isClearable
                    isSearchable={false}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={customSelectStyles}
                  />
                </div>
                </div>

                {/* Weight */}
                <div>
                  <label className="text-sm md:text-base lg:text-lg font-semibold text-gray-700 block mb-3 flex items-center gap-2 capitalize">
                    <span className="w-0.5 h-4 bg-orange-500 rounded-full"></span>
                    {petData.name ? (
                      <span>
                        <span className="text-orange-600 font-bold">{petData.name}</span>
                        <span className="text-gray-600">'s Weight</span>
                      </span>
                    ) : (
                      "Weight"
                    )}
                    <span className="text-xs text-gray-400">(kg)</span>
                  </label>
                  <div className="relative md:w-36 lg:w-40">
                    <input
                      type="text"
                      value={petData.weight_kg || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^(?:\d{0,3})(?:\.\d{0,2})?$/.test(value)) {
                          handleChange("weight_kg", value);
                        }
                      }}
                      placeholder="0.00"
                      className="w-full rounded-xl border border-gray-300 px-5 py-1.5 text-gray-700 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all premium-input shadow-sm"
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm md:text-base lg:text-lg">kg</div>
                  </div>
                </div>
              </div>

              {/* Age Section - Redesigned */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-3 border border-blue-100 mt-3">
                <div className="flex flex-col gap-3">
                  <label className="text-sm md:text-base lg:text-lg font-semibold text-gray-700 flex items-center gap-2 whitespace-nowrap capitalize">
                    <span className="w-0.5 h-4 bg-orange-500 rounded-full"></span>
                    {petData.name ? (
                      <span>
                        <span className="text-orange-600 font-bold">{petData.name}</span>
                        <span className="text-gray-600">'s Age</span>
                      </span>
                    ) : (
                      "Age"
                    )}
                  </label>
                  
                  {/* Toggle Buttons - Modern design */}
                  <div className="inline-flex w-fit bg-white rounded-xl p-1 shadow-sm">
                    <button
                      type="button"
                      onClick={() => setAgeMode("dob")}
                      className={`px-5 py-2 rounded-lg text-sm md:text-base lg:text-lg font-medium transition-all ${
                        ageMode === "dob"
                          ? "bg-orange-500 text-white shadow-md"
                          : "text-gray-600 hover:text-orange-500"
                      }`}
                    >
                      Select DOB
                    </button>
                    <button
                      type="button"
                      onClick={() => setAgeMode("manual")}
                      className={`px-5 py-2 rounded-lg text-sm md:text-base lg:text-lg font-medium transition-all ${
                        ageMode === "manual"
                          ? "bg-orange-500 text-white shadow-md"
                          : "text-gray-600 hover:text-orange-500"
                      }`}
                    >
                      ✏️ Enter Age
                    </button>
                  </div>

                  {/* Input Area */}
                  <div className="w-full lg:w-auto ">
                    {ageMode === "manual" ? (
                      <div className="flex gap-2">
                        <AgeScrollDropdown
                          value={manualAge.years ?? ""}
                          onChange={(value) => handleManualAge("years", value)}
                          placeholder="Years"
                          options={Array.from({ length: 31 }, (_, i) => ({
                            value: i,
                            label: `${i} ${i === 1 ? 'year' : 'years'}`,
                          }))}
                        />
                        <AgeScrollDropdown
                          value={manualAge.months ?? ""}
                          onChange={(value) => handleManualAge("months", value)}
                          placeholder="Months"
                          options={Array.from({ length: 12 }, (_, i) => ({
                            value: i,
                            label: `${i} ${i === 1 ? 'month' : 'months'}`,
                          }))}
                        />
                        <AgeScrollDropdown
                          value={manualAge.days ?? ""}
                          onChange={(value) => handleManualAge("days", value)}
                          placeholder="Days"
                          options={Array.from({ length: 31 }, (_, i) => ({
                            value: i,
                            label: `${i} ${i === 1 ? 'day' : 'days'}`,
                          }))}
                        />
                      </div>
                    ) : (
                      <div className="flex flex-wrap items-center gap-3">
                        <input
                          type="date"
                          max={new Date().toISOString().split("T")[0]}
                          className="w-full sm:w-auto min-w-[180px] max-w-[220px] rounded-xl border border-gray-300 px-3 py-2 text-sm md:text-base focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                          value={petData.birth_date || ""}
                          onChange={handleDob}
                        />
                        {petData.birth_date && (
                          <div className="bg-white rounded-xl px-4 py-2.5 border border-blue-200 shadow-sm">
                            <span className="text-sm md:text-base lg:text-lg font-medium text-blue-600">
                              🎂 {formatAge(years, months, days)}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

          {/* Appearance Section - Modern grid */}
          <div className="mt-6">
            <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200">
              <div className="w-0.5 h-6 bg-gradient-to-b from-orange-500 to-orange-600 rounded-full"></div>
              <h3 className="text-lg font-semibold text-gray-800">Appearance Details</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 md:gap-6 lg:gap-3">
              {/* Color */}
              <div>
                <label className="text-sm md:text-base lg:text-lg font-semibold text-gray-700 block mb-3 flex items-center gap-2 capitalize">
                  <span className="w-0.5 h-4 bg-orange-500 rounded-full"></span>
                  {petData.name ? (
                    <span>
                      <span className="text-orange-600 font-bold">{petData.name}</span>
                      <span className="text-gray-600">'s Color</span>
                    </span>
                  ) : (
                    "Color"
                  )}
                </label>
                <div className="flex flex-wrap items-center gap-3">
                  {colors.map((c) => (
                    <button
                      key={c}
                      onClick={() => handleChange("color", c)}
                      className={`w-6 h-6 rounded-full transition-all duration-200 ${
                        petData.color === c
                          ? "ring-2 ring-offset-2 ring-orange-500 scale-110 shadow-lg"
                          : "hover:scale-105"
                      }`}
                      style={{ backgroundColor: c }}
                      title={c}
                    />
                  ))}
                  <button
                    type="button"
                    onClick={() => setShowColorInput(!showColorInput)}
                    className="w-8 h-8 rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 hover:border-orange-500 hover:text-orange-500 transition-all"
                  >
                    +
                  </button>
                </div>
                {showColorInput && (
                  <div className="flex gap-2 mt-3">
                    <input
                      type="text"
                      placeholder="Custom color (name or hex)"
                      value={customColor}
                      onChange={(e) => setCustomColor(e.target.value)}
                      className="flex-1 rounded-xl border border-gray-300 px-3 py-2 text-sm md:text-base lg:text-lg focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (!customColor.trim()) return;
                        setColors((prev) => [...prev, customColor]);
                        handleChange("color", customColor);
                        setCustomColor("");
                        setShowColorInput(false);
                      }}
                      className="px-4 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white font-medium hover:shadow-md transition-all"
                    >
                      Add
                    </button>
                  </div>
                )}
              </div>

              {/* Hair Length */}
              <div>
                <label className="text-sm md:text-base lg:text-lg font-semibold text-gray-700 block mb-2 flex items-center gap-2 capitalize">
                  <span className="w-0.5 h-4 bg-orange-500 rounded-full"></span>
                  {petData.name ? (
                    <span>
                      <span className="text-orange-600 font-bold">{petData.name}</span>
                      <span className="text-gray-600">'s Hair Length</span>
                    </span>
                  ) : (
                    "Hair Length"
                  )}
                </label>
                <div className="hidden lg:flex flex-wrap gap-3">
                  {["Short", "Medium", "Long"].map((length) => {
                    const active = petData.hair_length === length;
                    return (
                      <button
                        key={length}
                        type="button"
                        onClick={() => handleChange("hair_length", length)}
                        className={`px-3 py-2.5 rounded-xl text-sm md:text-base lg:text-lg font-medium transition-all ${
                          active
                            ? "bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-md"
                            : "bg-gray-50 text-gray-600 border border-gray-200 hover:bg-orange-50"
                        }`}
                      >
                        {length}
                      </button>
                    );
                  })}
                </div>
                 {/* Dropdown for mobile screens */}
                <div className="lg:hidden">
                  <Select
                    options={[
                      { value: "Short", label: "Short" },
                      { value: "Medium", label: "Medium" },
                      { value: "Long", label: "Long" }
                    ]}
                    value={
                      petData.hair_length
                        ? { value: petData.hair_length, label: petData.hair_length }
                        : null
                    }
                    onChange={(option) =>
                      handleChange("hair_length", option?.value || "")
                    }
                    placeholder="Select Hair Length"
                    isClearable
                    isSearchable={false}

                    menuPortalTarget={document.body}
                    menuPosition="fixed"

                    styles={customSelectStyles}
                  />
                </div>
              </div>
              
              {/* Hair Type - Full width */}
              <div className="md:col-span-1">
                <label className="text-sm md:text-base lg:text-lg font-semibold text-gray-700 block mb-3 flex items-center gap-2 capitalize">
                  <span className="w-0.5 h-4 bg-orange-500 rounded-full"></span>
                  {petData.name ? (
                    <span>
                      <span className="text-orange-600 font-bold">{petData.name}</span>
                      <span className="text-gray-600">'s Hair Type</span>
                    </span>
                  ) : (
                    "Hair Type"
                  )}
                </label>
                <div className=" lg:hidden lg:flex flex-wrap gap-3">
                  {["Smooth", "Wire", "Double", "Hairless", "Curly", "Wavy", "Drop Coat"].map((type) => {
                    const active = petData.hair_type === type;
                    return (
                      <button
                        key={type}
                        type="button"
                        onClick={() => handleChange("hair_type", type)}
                        className={`px-3 py-2.5 rounded-xl text-sm md:text-sm lg:text-base font-medium transition-all ${
                          active
                            ? "bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-md"
                            : "bg-gray-50 text-gray-600 border border-gray-200 hover:bg-orange-50"
                        }`}
                      >
                        {type}
                      </button>
                    );
                  })}
                </div>
                   {/* Dropdown for mobile screens */}
                    <div className="">
                     <Select
                      options={[
                        "Smooth", "Wire", "Double", "Hairless", "Curly", "Wavy", "Drop Coat"
                      ].map(type => ({ value: type, label: type }))}
                      value={
                        petData.hair_type
                          ? { value: petData.hair_type, label: petData.hair_type }
                          : null
                      }
                      onChange={(option) =>
                        handleChange("hair_type", option?.value || "")
                      }
                      placeholder="Select Hair Type"
                      isClearable
                      isSearchable={false}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={customSelectStyles}
                    />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        {/* Card 3: Health & Lifestyle */}
        <div className="premium-card bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mt-3">
            {/* Header */}
            <div className="bg-gradient-to-r from-gray-100 to-gray-100 px-3 py-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-blue-600">Health & Lifestyle</h2>
              </div>
            </div>
            
            <div className="p-3 space-y-3">
              {/* Health Status */}
              <div>
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-200">
                  <div className="w-0.5 h-5 bg-gradient-to-b from-green-500 to-green-600 rounded-full"></div>
                  <h3 className="text-lg font-semibold text-gray-800">Health Status</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 lg:gap-14">
                  {/* Neutered Status */}
                  <div>
                    <label className="text-sm md:text-base lg:text-lg font-semibold text-gray-700 block mb-3 flex items-center gap-2 capitalize">
                      <span className="w-0.5 h-4 bg-orange-500 rounded-full"></span>
                      {petData.name ? (
                        <span>
                          <span className="text-orange-600 font-bold">{petData.name}</span>
                          <span className="text-gray-600">'s Neutered Status</span>
                        </span>
                      ) : (
                        "Neutered Status"
                      )}
                    </label>
                    <div className="lg:hidden lg:flex flex-wrap gap-3">
                      {["Not Neutered", "Neutered", "Unknown"].map((s) => {
                        const active = petData.neutered_spayed === s;
                        return (
                          <button
                            key={s}
                            type="button"
                            onClick={() => handleChange("neutered_spayed", s)}
                            className={`flex-1 px-4 py-2.5 rounded-xl text-sm md:text-base lg:text-lg font-medium transition-all ${
                              active
                                ? "bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-md"
                                : "bg-gray-50 text-gray-600 border border-gray-200 hover:bg-orange-50"
                            }`}
                          >
                            {s}
                          </button>
                        );
                      })}
                    </div>
                    {/* Dropdown for mobile screens */}
                    <div className="">
                     <Select
                      options={[
                        "Not Neutered", "Neutered", "Unknown"
                      ].map(s => ({ value: s, label: s }))}
                      value={
                        petData.neutered_spayed
                          ? { value: petData.neutered_spayed, label: petData.neutered_spayed }
                          : null
                      }
                      onChange={(option) =>
                        handleChange("neutered_spayed", option?.value || "")
                      }
                      placeholder="Select Status"
                      isClearable
                      isSearchable={false}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={customSelectStyles}
                    />
                    </div>
                  </div>

                  {/* Vaccination Status */}
                  <div>
                    <label className="text-sm md:text-base lg:text-lg font-semibold text-gray-700 block mb-3 flex items-center gap-2 capitalize">
                        <span className="w-0.5 h-4 bg-orange-500 rounded-full"></span>
                        {petData.name ? (
                          <span>
                            <span className="text-orange-600 font-bold">{petData.name}</span>
                            <span className="text-gray-600">'s Vaccination Status</span>
                          </span>
                        ) : (
                          "Vaccination Status"
                        )}
                      </label>
                    <div className="lg:hidden lg:flex flex-wrap gap-3">
                      {["Vaccinated", "Not Vaccinated", "Unknown"].map((v) => {
                        const active = petData.vaccination_status === v;
                        return (
                          <button
                            key={v}
                            type="button"
                            onClick={() => handleChange("vaccination_status", v)}
                            className={`flex-1 px-4 py-2.5 rounded-xl text-sm md:text-base lg:text-lg font-medium transition-all ${
                              active
                                ? "bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-md"
                                : "bg-gray-50 text-gray-600 border border-gray-200 hover:bg-orange-50"
                            }`}
                          >
                            {v}
                          </button>
                        );
                      })}
                    </div>
                       {/* Dropdown for mobile screens */}
                    <div className="">
                     <Select
                      options={[
                        "Vaccinated", "Not Vaccinated", "Unknown"
                      ].map(v => ({ value: v, label: v }))}
                      value={
                        petData.vaccination_status
                          ? { value: petData.vaccination_status, label: petData.vaccination_status }
                          : null
                      }
                      onChange={(option) =>
                        handleChange("vaccination_status", option?.value || "")
                      }
                      placeholder="Select Vaccination Status"
                      isClearable
                      isSearchable={false}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={customSelectStyles}
                    />
                    </div>
                  </div>
                </div>
              </div>

            {/* Diet & Routine */}
            <div>
              <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-200">
                <div className="w-0.5 h-5 bg-gradient-to-b from-green-500 to-green-600 rounded-full"></div>
                <h3 className="text-lg font-semibold text-gray-800">Diet & Routine</h3>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-6">
                {/* Meal Preferences */}
                <div className="space-y-2">
                <label className="text-sm md:text-base lg:text-lg font-semibold text-gray-700 block mb-3 flex items-center gap-2 capitalize">
                    <span className="w-0.5 h-4 bg-orange-500 rounded-full"></span>
                    {petData.name ? (
                      <span>
                        <span className="text-orange-600 font-bold">{petData.name}</span>
                        <span className="text-gray-600">'s Meal Preferences</span>
                      </span>
                    ) : (
                      "Meal Preferences"
                    )}
                  </label>
                  <div className="grid grid-cols-2 gap-1">
                    {mealOptions.map((item) => {
                      const checked = petData.meal_preferences?.includes(item.value);
                      return (
                        <label key={item.value} className="flex items-center gap-2 cursor-pointer p-1 rounded-lg hover:bg-gray-50 transition-all">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const updated = e.target.checked
                                ? [...(petData.meal_preferences || []), item.value]
                                : petData.meal_preferences.filter((v) => v !== item.value);
                              handleChange("meal_preferences", updated);
                            }}
                            className="w-5 h-5 rounded border-gray-300 text-orange-500 focus:ring-orange-500 focus:ring-2"
                          />
                          <span className={`w-3 h-3 rounded-full ${item.color === "green" ? "bg-green-500" : "bg-red-500"}`} />
                          <span className="text-sm md:text-base lg:text-lg text-gray-700 font-medium">{item.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Meals & Walks */}
                <div className="space-y-3">
                  <div>
                    <label className="text-sm md:text-base lg:text-lg font-semibold text-gray-700 block mb-2 flex items-center gap-2 capitalize">
                        <span className="w-0.5 h-4 bg-orange-500 rounded-full"></span>
                        {petData.name ? (
                          <span>
                            <span className="text-orange-600 font-bold">{petData.name}</span>
                            <span className="text-gray-600">'s Meals per Day</span>
                          </span>
                        ) : (
                          "Meals per Day"
                        )}
                      </label>
                    <div className="relative">
                      <input
                        type="text"
                        inputMode="numeric"
                        className="w-full rounded-xl border border-gray-300 px-4 py-3.5 text-gray-700 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all premium-input shadow-sm"
                        placeholder="Number of meals"
                        value={petData.meals_per_day}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d*$/.test(value)) {
                            handleChange("meals_per_day", value);
                          }
                        }}
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm md:text-base lg:text-lg">per day</div>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm md:text-base lg:text-lg font-semibold text-gray-700 block mb-2 flex items-center gap-2 capitalize">
                        <span className="w-0.5 h-4 bg-orange-500 rounded-full"></span>
                        {petData.name ? (
                          <span>
                            <span className="text-orange-600 font-bold">{petData.name}</span>
                            <span className="text-gray-600">'s Walks per Day</span>
                          </span>
                        ) : (
                          "Walks per Day"
                        )}
                      </label>
                    <div className="relative">
                      <input
                        type="text"
                        inputMode="numeric"
                        className="w-full rounded-xl border border-gray-300 px-4 py-3.5 text-gray-700 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all premium-input shadow-sm"
                        placeholder="Number of walks"
                        value={petData.walks_per_day}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d*$/.test(value)) {
                            handleChange("walks_per_day", value);
                          }
                        }}
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm md:text-base lg:text-lg">per day</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Behaviour Notes */}
            <div>
              <label className="text-sm md:text-base lg:text-lg font-semibold text-gray-700 block mb-2 flex items-center gap-2 capitalize">
                <span className="w-0.5 h-4 bg-orange-500 rounded-full"></span>
                {petData.name ? (
                  <span>
                    <span className="text-orange-600 font-bold">{petData.name}</span>
                    <span className="text-gray-600">'s Notes</span>
                  </span>
                ) : (
                  "Behaviour Notes"
                )}
              </label>
              <textarea
                rows={3}
                maxLength={250}
                className="w-full rounded-xl border border-gray-300 px-4 py-3 text-gray-700 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all resize-none premium-input shadow-sm"
                placeholder="Share any special notes about your pet's behavior, personality, or preferences..."
                value={petData.behaviour}
                onChange={(e) => handleChange("behaviour", e.target.value)}
              />
              <div className="mt-2 flex justify-between items-center text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-20 h-1 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-orange-500 rounded-full transition-all" style={{ width: `${(petData.behaviour?.length || 0) / 250 * 100}%` }}></div>
                  </div>
                  <span className="text-gray-500">{petData.behaviour?.length || 0} / 250 characters</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>

      {/* Sticky Footer */}
      <div className="sticky bottom-0 left-0 right-0 mt-8">
        <div className="bg-white/90 backdrop-blur-md border-t border-gray-200 px-4 py-3 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto flex justify-end gap-3">
            {/* Cancel Button */}
            <button
              onClick={onSave}
              type="button"
              className="px-5 py-2 text-sm sm:text-base font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 rounded-lg transition"
            >
              Cancel
            </button>

            {/* Save Button */}
            <button
              onClick={handleSave}
              disabled={!isDirty || loading}
              type="button"
              className={`px-6 py-2 text-sm sm:text-base font-semibold rounded-lg transition flex items-center gap-2.5 shadow-md ${
                isDirty && !loading
                  ? "bg-orange-500 text-white hover:bg-orange-600 active:bg-orange-700"
                  : "bg-orange-200 text-white/80 cursor-not-allowed"
              }`}
            >
              {loading ? (
                <>
                  <svg
                    className="animate-spin -ml-1 h-4 w-4 sm:h-5 sm:w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  <span className="hidden xs:inline">Saving...</span>
                  <span className="xs:hidden">...</span>
                </>
              ) : petToEdit ? (
                <>
                  <span className="hidden xs:inline">Save Changes</span>
                  <span className="xs:hidden">Save</span>
                </>
              ) : (
                <>
                  <span className="hidden xs:inline">Add Pet</span>
                  <span className="xs:hidden">Add</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BasicinfopetForm;
